import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { COMMON_TRANSLATION } from '@constants/index';
import { FactorType, MY_ACCOUNT_TRANSLATION } from '@modules/my-account/constants';
import { Loader, NotificationVariants, PageTitle, Card, Page } from '@base-app';
import { useAddNotification } from '@modules/notifications/store';
import { useAuthStore } from '@modules/auth/store';
import { useMutationCurrentUser } from '@modules/core/hooks';
import { useQueryFactors } from '@modules/my-account/hooks';
import { User } from '@modules/core/types';

import PersonalInformationForm from '@modules/my-account/components/PersonalInformationForm/PersonalInformationForm';
import useOrganizationName from '@modules/my-account/hooks/useOrganizationName';

const MyAccountScreen: FC = () => {
  const { t } = useTranslation(MY_ACCOUNT_TRANSLATION);
  const { data: factors, isLoading: isLoadingFactors } = useQueryFactors();
  const { user: currentUser, updateUser } = useAuthStore();

  const [updatedUser, setUpdatedUser] = useState<User | null>(null);

  const organizationName = useOrganizationName();
  const addNotification = useAddNotification();

  const phoneNumber = useMemo(() => {
    return factors?.find((factor) => factor.factorType === FactorType.SMS)?.profile.phoneNumber;
  }, [factors]);

  const handleUpdateUserMutationSuccess = (): void => {
    addNotification({
      autoClose: true,
      title: t('user_updated_success'),
      type: NotificationVariants.SUCCESS,
    });
    if (updatedUser !== null) updateUser(updatedUser);
  };

  const handleUpdateUserMutationError = (): void => {
    addNotification({
      autoClose: true,
      title: t('errors.generic', { ns: COMMON_TRANSLATION }),
      type: NotificationVariants.ERROR,
    });
    setUpdatedUser(null);
  };

  const { mutateAsync: updateCurrentUser } = useMutationCurrentUser(
    handleUpdateUserMutationSuccess,
    handleUpdateUserMutationError,
  );

  const handleOnSaveForm = async (user: User): Promise<void> => {
    const updatedUser: User = {
      ...user,
      profile: {
        ...user.profile,
        firstName: user.profile.firstName,
        lastName: user.profile.lastName,
      },
    };
    setUpdatedUser(updatedUser);

    await updateCurrentUser(updatedUser);
  };

  useEffect(() => {
    setUpdatedUser(currentUser);
  }, [currentUser]);

  return (
    <Page title={t('my_account', { ns: COMMON_TRANSLATION })}>
      <Card
        css={{
          padding: '$8',
        }}
      >
        {isLoadingFactors ? (
          <Loader css={{ height: '520px' }} />
        ) : (
          <>
            <PageTitle
              css={{
                marginBottom: '40px',
              }}
            >
              {t('personal_information')}
            </PageTitle>
            <PersonalInformationForm
              currentUser={updatedUser}
              onSaveForm={handleOnSaveForm}
              organizationName={organizationName}
              phoneNumber={phoneNumber}
            />
          </>
        )}
      </Card>
    </Page>
  );
};

export default MyAccountScreen;
